<template>
  <div class="mainRecordSetting">
    <setting-header />
    <v-divider class="mainRecordSetting_header-divider" />
    <div class="mainRecordSetting_period">
      <h5>録画映像の期間</h5>
      <span class="mainRecordSetting_period-daterange">{{ periodRange }}</span>
    </div>
    <v-divider />
    <div class="mainRecordSetting_select-period">
      <h5>ダウンロードする期間の選択</h5>
      <span class="mainRecordSetting_caption">
        一度にダウンロードできる映像は、1週間分までとなります。
      </span>
      <div class="mainRecordSetting_select-period--date">
        <DateRangePicker
          flexible-range
          :min-date="minDate"
          :max-date="maxDate"
          :min-range="10"
          :max-range-day="7"
          :dates="downloadTimePeriod"
          :active="hasDate"
          @applySetDates="applySetDates($event)"
        />
      </div>
      <span class="mainRecordSetting_select-period--value"
        >選択した期間：{{ selectedPeriod }}</span
      >
    </div>
    <v-divider />
    <div class="mainRecordSetting_cam">
      <div
        v-ripple
        class="mainRecordSetting_cam-collapse"
        @click="handleDownloadSelect"
      >
        <h5>ダウンロードするカメラ</h5>
        <v-icon class="mainRecordSetting_cam-collapse--icon" size="22">
          mdi-chevron-right
        </v-icon>
      </div>
      <div v-if="timelines.length > 0" class="mainRecordSetting_cam-timelines">
        <div
          v-for="(timeline, index) in timelines"
          :key="index"
          class="mainRecordSetting_cam-timeline"
        >
          <div class="mainRecordSetting_cam-timeline--header">
            <span class="mainRecordSetting_cam-timeline--name">{{
              timeline.name
            }}</span>
            <span class="mainRecordSetting_cam-timeline--size">
              {{ timeline.size }}</span
            >
          </div>
          <timeline-event
            v-if="!hideTimelines"
            :events="camEvents[timeline.id] || []"
            :period-start="dates[0]"
            :period-end="dates[1]"
            :start="timeline.start"
            :end="timeline.end"
            class="mainRecordSetting_cam-timeline--event"
          />
        </div>
      </div>
    </div>
    <v-divider />
    <div class="mainRecordSetting_file-size">
      <h5>ファイルサイズ</h5>
      <span class="mainRecordSetting_file-size--total">合計：約</span>
      <span class="mainRecordSetting_file-size--total-value">
        {{ totalCapacity }}</span
      >
      <br />
      <v-btn
        class="mainRecordSetting_download-btn"
        :disabled="selectedRecords.length == 0"
        :loading="createRecordsLoading"
        @click="handleDownload"
      >
        ダウンロードをリクエストする</v-btn
      >
    </div>
    <v-divider />
    <template v-for="(record, index) in recordList">
      <record-download-status :key="index" :record="record" />
      <v-divider :key="`div-${index}`" />
    </template>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import SettingHeader from './SettingHeader.vue';
import RecordDownloadStatus from './RecordDownloadStatus.vue';
import DateRangePicker from '@/components/inputs/DateRangePicker.vue';
import {
  getDefaultCalendarRecord,
  getDownloadTimePeriod,
  getRecordTimelineRange,
  secondsToTime,
} from '@/utils/date';
import TimelineEvent from '@/components/TimelineEvent.vue';
import { mapActions, mapState } from 'vuex';
import { parseTimelineEvents } from '@/utils/event';
import { bytesToGB } from '@/utils/helpers';

export default {
  name: 'MainRecordSetting',
  components: {
    SettingHeader,
    RecordDownloadStatus,
    DateRangePicker,
    TimelineEvent,
  },
  props: {
    selectedRecords: {
      type: Array,
      default: () => [],
    },
    selectedCams: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      hasDate: false,
      minDate: dayjs().subtract(1, 'year').toDate(),
      maxDate: dayjs().toDate(),
      dates: getDefaultCalendarRecord(),
      downloadTimePeriod: getDownloadTimePeriod(),
      isDownloadaing: true,
      camEvents: {},
      createRecordsLoading: false,
      hideTimelines: false,
      hideTimelinesTimeout: null,
    };
  },
  computed: {
    ...mapState({
      cameraList: (state) => state.common.cameraList,
      recordEvents: (state) => state.event.recordEvents,
      records: (state) => state.record.downloadRcords,
    }),

    timelines() {
      if (this.cams.length === 0) {
        return [];
      }
      return this.cams.map((cam) => {
        const { bitrate, name, id } = cam;
        const approx = this.getSizeBasedOnBitrate(
          bitrate,
          this.diffPeriodInSeconds
        );
        const capacity = this.getSizeBasedOnBitrate(
          bitrate,
          this.retentionPeriodTotalInSeconds
        );
        let start = this.timelineRange.start.format('YYYY-MM-DDTHH:mm:ss');
        let end = this.timelineRange.end.format('YYYY-MM-DDTHH:mm:ss');

        return {
          id,
          name,
          start,
          end,
          size: `${bytesToGB(approx).toUpperCase()} / ${bytesToGB(
            capacity
          ).toUpperCase()}`,
          capacity,
          approx,
        };
      });
    },
    countSelectedDays() {
      return dayjs.duration(dayjs(this.dates[1]).diff(this.dates[0])).asDays();
    },
    retentionPeriodTotal() {
      const startDate = dayjs()
        .subtract(7, 'days')
        .set('hours', 0)
        .set('minutes', 0)
        .set('seconds', 0);
      const endDate = dayjs()
        .set('hours', 0)
        .set('minutes', 0)
        .set('seconds', 0);
      return endDate.diff(startDate);
    },
    periodRange() {
      const startDate = dayjs()
        .subtract(1, 'years')
        .set('hours', 0)
        .set('minutes', 0)
        .set('seconds', 0);
      const endDate = dayjs().set('seconds', 0);
      return `${startDate.format('YYYY/MM/DD HH:mm:ss')} ~ ${endDate.format(
        'YYYY/MM/DD HH:mm:ss'
      )}`;
    },
    timelineRange() {
      return getRecordTimelineRange(this.dates[0], this.dates[1]);
    },
    diffPeriod() {
      return dayjs(this.dates[1]).diff(dayjs(this.dates[0]));
    },
    diffPeriodInSeconds() {
      return dayjs.duration(this.diffPeriod).asSeconds();
    },
    retentionPeriodTotalInSeconds() {
      return dayjs.duration(this.retentionPeriodTotal).asSeconds();
    },
    selectedPeriod() {
      const timePeriod = secondsToTime(this.diffPeriodInSeconds);
      return timePeriod[1] > 0
        ? `${timePeriod[0]}時間${timePeriod[1]}分`
        : `${timePeriod[0]}時間`;
    },
    totalCapacity() {
      if (this.timelines.length === 0) {
        return '0.00GB';
      }
      const total = this.timelines.reduce(
        (curr, item) => curr + item.approx,
        0
      );
      return bytesToGB(total).toUpperCase();
    },
    cams() {
      return Object.keys(this.selectedCams)
        .filter((key) => this.selectedCams[key])
        .map((key) => this.cameraList[key]);
    },
    recordList() {
      // Descending order of download request time
      return Object.values(this.records).sort(
        (a, b) => b.createDateUnix - a.createDateUnix
      );
    },
  },
  watch: {
    cams: {
      handler(val) {
        if (val) {
          this.fetchEvents();
        }
      },
      deep: true,
    },
    dates: {
      handler(val) {
        if (val) {
          this.fetchEvents();
          this.resetTimelines();
        }
      },
      deep: true,
    },
  },
  created() {
    const { start, end } = this.$route.query;
    if (start && end) {
      this.dates = [start, end];
    }
  },
  mounted() {
    this.fetchEvents();
    this.fetchDownloadRecords();
  },
  methods: {
    ...mapActions('event', ['fetchRecordEvents']),
    ...mapActions('record', ['createDownloadRecords', 'fetchDownloadRecords']),
    testAbc(e) {
      console.log(e);
    },
    resetTimelines() {
      if (this.hideTimelinesTimeout) {
        clearTimeout(this.hideTimelinesTimeout);
      }
      this.hideTimelines = true;
      this.hideTimelinesTimeout = setTimeout(() => {
        this.hideTimelines = false;
      }, 200);
    },
    async fetchEvents() {
      if (this.cams.length === 0) {
        return;
      }
      const params = {
        cameraIds: this.cams.map((cam) => cam.id),
        searchKeys: [],
        offset: 0,
        upperLimit: 0,
        startDate: this.timelineRange.start.format('YYYYMMDDHHmmss'),
        endDate: this.timelineRange.end.format('YYYYMMDDHHmmss'),
      };
      await this.fetchRecordEvents(params);
      this.mapCamEvents();
    },
    mapCamEvents() {
      this.cams.forEach((cam) => {
        this.$set(
          this.camEvents,
          cam.id,
          parseTimelineEvents(
            this.recordEvents.filter((event) => event.cameraId === cam.id)
          )
        );
      });
    },
    getSizeBasedOnBitrate(bitrate, retentionPeriod) {
      return ((bitrate / 8) * retentionPeriod) / 1000;
    },
    handleDownloadSelect() {
      this.$router.push({
        name: 'settings-record',
        params: { child: 'download' },
        query: { start: this.dates[0], end: this.dates[1] },
      });
    },
    async handleDownload() {
      try {
        this.createRecordsLoading = true;
        await this.createDownloadRecords({
          cameraIds: this.cams.map((cam) => cam.id).join(','),
          startDate: this.dates[0],
          endDate: this.dates[1],
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.createRecordsLoading = false;
      }
    },
    applySetDates(event = []) {
      if (event.length) {
        this.downloadTimePeriod = event;
      }
    },
  },
};
</script>

<style lang="scss">
.mainRecordSetting {
  &_header-divider {
    margin-top: 15px;
  }
  & .dateRangePicker_dates {
    width: unset;
    flex-wrap: nowrap;
    & .dateRangePicker_separator {
      margin-right: 10px;
    }
  }

  &_caption {
    display: block;
    margin-top: 5px;
    font-size: 12px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.33;
    letter-spacing: normal;
    text-align: left;
    color: #989898;
  }
  &_cam {
    padding: 15px 0;
    &-collapse {
      cursor: pointer;
      width: 200px;
      display: flex;
      align-items: center;
      border-radius: 5px;
      &--icon {
        margin-left: 5px;
        color: #c1c1c1 !important;
      }
    }
    &-timelines {
      margin-top: 15px;
    }
    &-timeline {
      margin-bottom: 15px;
      width: 345px;
      &--header {
        display: flex;
        justify-content: space-between;
      }
      &--name {
        font-size: 12px;
        font-weight: bold;
        font-stretch: normal;
        font-style: normal;
        letter-spacing: normal;
        float: left;
        color: #4b4b4b;
      }
      &--size {
        font-size: 12px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        letter-spacing: normal;
        float: right;
        color: #666;
      }
      &--event {
        margin-left: 7px;
        margin-top: 14px;
      }
    }
  }

  h5 {
    font-size: 14px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.14;
    letter-spacing: normal;
    text-align: left;
    color: #4b4b4b;
  }
  &_period {
    padding: 15px 0;
    &-daterange {
      display: block;
      margin-top: 10px;
      font-size: 16px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1;
      letter-spacing: normal;
      text-align: left;
      color: #4b4b4b;
    }
  }
  &_select-period {
    padding: 15px 0;
    &--date {
      max-width: 400px;
      flex-wrap: nowrap;
      .dateRangePicker_date,
      .dateRangePicker_time {
        font-size: 16px;
        margin-right: 10px;
        color: #0075c2;
      }
      .dateRangePicker_separator {
        font-size: 16px;
        color: #989898;
      }
      .icon {
        color: #0075c2;
      }
      .v-input--checkbox {
        margin-top: 0;
        padding-top: 10px;
        .v-messages {
          display: none;
        }
        .v-input__slot {
          margin-bottom: 0;
        }
      }
    }
    &--value {
      font-size: 14px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.14;
      letter-spacing: normal;
      text-align: left;
      color: #4b4b4b;
      margin-top: 11px;
      display: block;
    }
  }
  &_file-size {
    padding: 15px 0;
    &--total,
    &--total-value {
      margin-top: 10px;
      display: inline-block;
      font-size: 14px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1;
      letter-spacing: normal;
      text-align: left;
      color: #4b4b4b;
    }
    &--total-value {
      font-size: 16px;
      margin-left: 14px;
    }
  }
  &_download-btn {
    margin-top: 31px;
    font-size: 14px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.14;
    letter-spacing: normal;
    text-align: left;
    border-radius: 5px;
    width: 228px;
    height: 36px;
    &:disabled {
      color: #ccc;
      background-color: #f3f3f3;
    }
  }
  .theme--light.v-btn.v-btn--disabled.v-btn--has-bg {
    height: 36px;
    background-color: #f3f3f3 !important;
  }
}
</style>
